import { Box, Stack, Typography } from '@mui/material'
import { useChartKnobsState } from '~/state/useChartKnobsState'
import { ChartLegend } from './ChartLegend'
import {
  MESSAGES,
  campaignNoDataNotice,
  campaignNoRowsNotice,
} from '~/components/Charts/chrome/chartChrome'
import { DataValue } from '~/components/Charts/chrome/DataValue'
import { GrowthChip } from '~/components/Charts/chrome/GrowthChip'
import type { Group } from './dataPipeline'

// ─── Pure builder ────────────────────────────────────────────────────────────

export interface MatrixCell {
  seriesKey: string
  label: string
  colorHex: string
  sum: number | null
  growth: number | null
  /** 'neg' when the cell's Σ is negative, 'pos' otherwise (incl. 0 and null). Selects which
   *  zone of the diverging track the bar renders in. */
  direction: 'pos' | 'neg'
  /** Percent of the cell's OWN zone (left zone for 'neg', right zone for 'pos') — NOT percent
   *  of the whole track. All-positive matrices have a right zone spanning the full track width,
   *  so widthPct there is byte-identical to the pre-diverging |Σ|/globalMax formula. */
  widthPct: number
}

export interface MatrixColumn {
  label: string
  isBaseline: boolean
  cells: MatrixCell[]
}

export interface SummaryMatrix {
  columns: MatrixColumn[]
  /** Zero-axis position as a fraction of track width: maxNeg/(maxNeg+maxPos).
   *  0 = all-positive (axis at far left, pixel-parity with the pre-diverging layout).
   *  1 = all-negative (axis at far right). */
  zeroAxisFraction: number
}

/** Row 13: series × panel growth matrix. Δ chains per series vs the SAME series in the
 *  previous column; column 0 and baseline columns carry no Δ. Bars normalize PER SIGN ZONE:
 *  positive Σs normalize to the matrix's max positive Σ, negative Σs to the matrix's max
 *  |negative Σ| — each zone's own 100% mark, not a single global max (FU-50 surface 1:
 *  diverging geometry). An all-positive matrix has zeroAxisFraction 0 and a right zone spanning
 *  the whole track, so its widthPct is byte-identical to the pre-diverging |Σ|/globalMax formula. */
export function buildSummaryMatrix(
  perPanelSeriesSums: Array<{
    label: string
    isBaseline?: boolean
    sums: Map<string, number | null>
  }>,
  groups: Group[],
): SummaryMatrix {
  let maxPos = 0
  let maxNeg = 0
  for (const p of perPanelSeriesSums)
    for (const v of p.sums.values()) {
      if (v === null) continue
      if (v > maxPos) maxPos = v
      if (-v > maxNeg) maxNeg = -v
    }
  const zeroAxisFraction = maxNeg === 0 ? 0 : maxNeg / (maxNeg + maxPos)
  const columns = perPanelSeriesSums.map((p, i) => ({
    label: p.label,
    isBaseline: p.isBaseline ?? false,
    cells: groups.map((g) => {
      const sum = p.sums.get(g.key) ?? null
      const prevPanel = i > 0 ? perPanelSeriesSums[i - 1] : undefined
      const prev = prevPanel?.sums.get(g.key)
      // Δ vs same series in the previous column; baseline col and col 0 carry no Δ.
      const growth =
        i === 0 || p.isBaseline || sum === null || prev == null || prev === 0
          ? null
          : Math.round((sum / prev - 1) * 100 * 100) / 100
      const direction: 'pos' | 'neg' = sum !== null && sum < 0 ? 'neg' : 'pos'
      const zoneMax = direction === 'neg' ? maxNeg : maxPos
      return {
        seriesKey: g.key,
        label: g.label,
        colorHex: g.color.hex,
        sum,
        growth,
        direction,
        widthPct: sum === null || zoneMax === 0 ? 0 : (Math.abs(sum) / zoneMax) * 100,
      }
    }),
  }))
  return { columns, zeroAxisFraction }
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  columns: MatrixColumn[]
  groups: Group[]
  /** Stable ID unique to this matrix instance — drives the URL-keyed knobs state. */
  pinId: string
  /** I1b blank case: the active campaign's display label, or null/absent when no campaign
   *  predicate is active — mirrors EmptyChartState/DynamicsPanelRow's campaignLabel (existing
   *  copy stays when null). */
  campaignLabel?: string | null
  /** D-FEW-5: sharper variant of the campaign notice — the fact carries the Campaign dim but no
   *  row is tagged with THIS campaign's id. Default false ⇒ byte-identical to before this prop
   *  existed. */
  campaignHasNoRows?: boolean
  /** buildSummaryMatrix's zero-axis position (fraction of track width). 0 renders a single
   *  positive-only zone (no divider, pixel-parity with the pre-diverging layout). */
  zeroAxisFraction: number
}

export function SummarySeriesMatrix({
  columns,
  groups,
  pinId,
  campaignLabel,
  campaignHasNoRows,
  zeroAxisFraction,
}: Props) {
  const knobs = useChartKnobsState(pinId, 'timeline')
  const { inactiveSeries, setInactiveSeries } = knobs

  const activeSeriesKeys = new Set(
    groups.filter((g) => !inactiveSeries.includes(g.key)).map((g) => g.key),
  )

  if (columns.length === 0) {
    // Truly empty (no data at all) — no legend because there is nothing to toggle back to.
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {campaignLabel
            ? campaignHasNoRows
              ? campaignNoRowsNotice(campaignLabel)
              : campaignNoDataNotice(campaignLabel)
            : MESSAGES.noDataForFilters}
        </Typography>
      </Box>
    )
  }

  if (activeSeriesKeys.size === 0) {
    // All series toggled inactive — show the empty message BUT keep the legend so the
    // user can re-enable a series (inactiveSeries persists in URL-keyed knobs state).
    return (
      <Box>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
        </Box>
        <ChartLegend groups={groups} inactiveSeries={inactiveSeries} onChange={setInactiveSeries} />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
        {columns.map((col, i) => (
          <Box
            key={`col-${i}`}
            sx={{ flex: 1, minWidth: 0, opacity: col.isBaseline ? 0.55 : 1 }}
            data-baseline={col.isBaseline ? 'true' : undefined}
          >
            <Typography variant="caption" color="text.secondary" noWrap>
              {col.label}
            </Typography>
            {col.cells
              .filter((cell) => activeSeriesKeys.has(cell.seriesKey))
              .map((cell) => (
                <Box key={cell.seriesKey} sx={{ mb: 0.75 }}>
                  <Box sx={{ display: 'flex', height: 14, mb: 0.25 }}>
                    {zeroAxisFraction > 0 ? (
                      <>
                        <Box
                          sx={{
                            width: `${zeroAxisFraction * 100}%`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {cell.direction === 'neg' ? (
                            <Box
                              sx={{
                                height: 14,
                                width: `${Math.max(cell.widthPct, cell.sum !== null ? 1 : 0)}%`,
                                minWidth: cell.sum !== null ? 2 : 0,
                                bgcolor: cell.colorHex,
                                borderRadius: '4px 0 0 4px',
                              }}
                            />
                          ) : null}
                        </Box>
                        <Box
                          data-testid="strip-zero-axis"
                          sx={{ width: 0, borderLeft: '2px solid', borderColor: 'text.primary' }}
                        />
                      </>
                    ) : null}
                    <Box sx={{ flex: 1 }}>
                      {cell.direction === 'pos' ? (
                        <Box
                          sx={{
                            height: 14,
                            width: `${Math.max(cell.widthPct, cell.sum !== null ? 1 : 0)}%`,
                            minWidth: cell.sum !== null ? 2 : 0,
                            bgcolor: cell.colorHex,
                            borderRadius: zeroAxisFraction > 0 ? '0 4px 4px 0' : 0.5,
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {cell.sum !== null ? <DataValue value={cell.sum} glyph="Σ" size="sm" /> : 'Σ –'}
                    {cell.growth !== null ? (
                      <>
                        {' · '}
                        <GrowthChip growth={cell.growth} />
                      </>
                    ) : null}
                  </Typography>
                </Box>
              ))}
          </Box>
        ))}
      </Stack>
      <ChartLegend groups={groups} inactiveSeries={inactiveSeries} onChange={setInactiveSeries} />
    </Box>
  )
}
